/**
 * Shared helper for system attribute detection.
 *
 * [FEATURE 2026-02-11] WAHA integration creates internal attributes
 * (waha_whatsapp_chat_id, waha_whatsapp_jid, etc.) that should never
 * be visible to end users. This helper centralizes the prefix-based
 * detection so it can be reused in:
 *   - Vuex store getters (attributes.js) — filters attribute definitions
 *   - Kanban components — filters raw custom_attributes values
 *   - Any future component that iterates custom_attributes directly
 */

const SYSTEM_ATTRIBUTE_PREFIXES = ['waha_'];

/**
 * Check if an attribute key belongs to a system/internal attribute.
 * @param {string} key - The attribute key to check (e.g. 'waha_whatsapp_chat_id')
 * @returns {boolean} true if the key starts with any system prefix
 */
export const isSystemAttributeKey = key => {
  if (!key || typeof key !== 'string') return false;
  return SYSTEM_ATTRIBUTE_PREFIXES.some(prefix => key.startsWith(prefix));
};

/**
 * Filter an object of custom_attributes, removing system attributes.
 * @param {Object} attrs - The custom_attributes object (e.g. contact.custom_attributes)
 * @returns {Object} A new object with system attributes removed
 */
export const filterSystemAttributes = attrs => {
  if (!attrs || typeof attrs !== 'object') return {};
  return Object.fromEntries(
    Object.entries(attrs).filter(([key]) => !isSystemAttributeKey(key))
  );
};

export default {
  isSystemAttributeKey,
  filterSystemAttributes,
  SYSTEM_ATTRIBUTE_PREFIXES,
};
